import { Affix, Alert, Anchor, Group, Text, TextInput } from '@mantine/core';
import { Trans } from 'react-i18next';
import { DOCS_USER_BASE_URL } from '@/config';
import { useStore } from '@/store';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FiMessageSquare, FiX, FiSend, FiCopy, FiCheck, FiChevronDown, FiTrash2, FiRefreshCw, FiThumbsUp, FiThumbsDown, FiBook, FiStar, FiAlertCircle, FiAlertTriangle, FiLayers, FiDownload, FiClock, FiCornerDownLeft } from 'react-icons/fi';
import { t } from 'i18next';
import { ChatSend } from '@/api';
import {
  PanelWrapper,
  ResizeHandle,
  WelcomeScreen,
  WelcomeTitle,
  OptionGrid,
  OptionCard,
  OptionCardTitle,
  OptionCardDesc,
  HistoryList,
  HistoryItem,
  HistoryItemTitle,
  HistoryItemMeta,
  HistoryItemBadge,
  HistoryItemActions,
  HistoryEmpty,
  MessageList,
  BubbleWrapper,
  BubbleActions,
  UserBubble,
  AssistantBubble,
  ErrorBubble,
  CopyButton,
  FeedbackButton,
  ParticleWrap,
  ParticleOrigin,
  Particle,
  Timestamp,
  InputRow,
  TypingIndicator,
  TypingDot,
  FABWrapper,
  FAB,
  UnreadBadge,
  SendButton,
  ScrollToBottomBtn,
} from './styles';

import type { ChatIntent } from '@/store';

const CHAT_OPTIONS: Array<{
  id: ChatIntent;
  icon: React.ReactNode;
  titleKey: string;
  descKey: string;
  starterKey: string;
  replyKey: string;
}> = [
  { id: 'documentation',      icon: <FiBook size={16} color="#228be6" />,      titleKey: 'chat.welcome.documentation', descKey: 'chat.welcome.documentation.desc', starterKey: 'chat.welcome.documentation.starter', replyKey: 'chat.welcome.documentation.reply' },
  { id: 'feature-request',    icon: <FiStar size={16} color="#f59f00" />,      titleKey: 'chat.welcome.feature',       descKey: 'chat.welcome.feature.desc',       starterKey: 'chat.welcome.feature.starter', replyKey: 'chat.welcome.feature.reply'       },
  { id: 'bug-report',         icon: <FiAlertCircle size={16} color="#fa5252" />, titleKey: 'chat.welcome.bug',         descKey: 'chat.welcome.bug.desc',           starterKey: 'chat.welcome.bug.starter', replyKey: 'chat.welcome.bug.reply'           },
  { id: 'mapping-assistant',  icon: <FiLayers size={16} color="#40c057" />,    titleKey: 'chat.welcome.mapping',       descKey: 'chat.welcome.mapping.desc',       starterKey: 'chat.welcome.mapping.starter', replyKey: 'chat.welcome.mapping.reply'       },
];

const formatTime = (ts: number) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const Chat = () => {
  const [isOpen, toggleChat, messages, addMessage, removeMessage, setMessageFeedback, isLoading, setIsLoading, unreadCount, intent, setIntent, saveSession, loadSession, deleteSession, clearMessages, history] =
    useStore(({ chat: c }) => [
      c.isOpen,
      c.toggleChat,
      c.messages,
      c.addMessage,
      c.removeMessage,
      c.setMessageFeedback,
      c.isLoading,
      c.setIsLoading,
      c.unreadCount,
      c.intent,
      c.setIntent,
      c.saveSession,
      c.loadSession,
      c.deleteSession,
      c.clearMessages,
      c.history,
    ]);
  const [textContent, name, domain] = useStore(({ config: c }) => [
    c.textContent,
    c.name,
    c.domain,
  ]);

  const [input, setInput] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [animCounters, setAnimCounters] = useState<Record<string, number>>({});
  const [panelSize, setPanelSize] = useState<{ width: number; height: number }>(() => {
    try {
      const saved = localStorage.getItem('mm_chat_panel_size');
      return saved ? JSON.parse(saved) : { width: 360, height: 500 };
    } catch {
      return { width: 360, height: 500 };
    }
  });

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const resizeOrigin = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    resizeOrigin.current = { x: e.clientX, y: e.clientY, w: panelSize.width, h: panelSize.height };
    const onMove = (ev: MouseEvent) => {
      if (!resizeOrigin.current) return;
      const dx = resizeOrigin.current.x - ev.clientX;
      const dy = resizeOrigin.current.y - ev.clientY;
      setPanelSize({
        width: Math.max(300, Math.min(720, resizeOrigin.current.w + dx)),
        height: Math.max(350, Math.min(850, resizeOrigin.current.h + dy)),
      });
    };
    const onUp = () => {
      resizeOrigin.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    try { localStorage.setItem('mm_chat_panel_size', JSON.stringify(panelSize)); } catch (e) { /* storage unavailable */ }
  }, [panelSize]);

  useEffect(() => {
    if (!showScrollBtn) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setShowScrollBtn(!atBottom);
  }, []);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollBtn(false);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      toggleChat();
    }, 170);
  };

  const handleCopy = (id: string, content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const callAPI = async (history: Array<{ role: 'user' | 'assistant'; content: string }>) => {
    setIsLoading(true);
    try {
      const { text: reply } = await ChatSend(
        history,
        { textContent, templateName: name, domain },
        // Which card was clicked decides how the assistant behaves —
        // a documentation question should not be answered as a mapping one.
        intent ?? undefined,
      );
      addMessage('assistant', reply);
    } catch {
      addMessage('assistant', t('chat.error'), { isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    addMessage('user', text);
    setInput('');
    const history = messages
      .filter((m) => !m.isError)
      .map(({ role, content }) => ({ role, content }));
    history.push({ role: 'user', content: text });
    await callAPI(history);
  };

  const handleRetry = (errorMsgId: string) => {
    removeMessage(errorMsgId);
    const history = messages
      .filter((m) => m.id !== errorMsgId && !m.isError)
      .map(({ role, content }) => ({ role, content }));
    callAPI(history);
  };

  const handleRegenerate = (assistantMsgId: string) => {
    removeMessage(assistantMsgId);
    const history = messages
      .filter((m) => m.id !== assistantMsgId && !m.isError)
      .map(({ role, content }) => ({ role, content }));
    callAPI(history);
  };

  const lastAssistantId = [...messages].reverse().find((m) => m.role === 'assistant' && !m.isError)?.id;

  const handleExport = () => {
    const date = new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
    const header = `MorphMapper Chat Export\n${name ? `Template: ${name}  |  ` : ''}${date}\n${'─'.repeat(40)}\n\n`;
    const body = messages
      .filter((m) => !m.isError)
      .map((m) => {
        const label = m.role === 'user' ? 'You' : 'Assistant';
        const time = formatTime(m.timestamp);
        return `[${label}] ${time}\n${m.content}`;
      })
      .join('\n\n');
    const blob = new Blob([header + body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-export-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const PARTICLE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

  const handleFeedback = (msgId: string, feedback: 'up' | 'down') => {
    setMessageFeedback(msgId, feedback);
    const key = `${msgId}-${feedback}`;
    setAnimCounters((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
    setTimeout(() => setAnimCounters((prev) => ({ ...prev, [key]: 0 })), 550);
  };

  const handleOptionSelect = (opt: typeof CHAT_OPTIONS[number]) => {
    setIntent(opt.id);
    addMessage('user', t(opt.starterKey));
    // Answered locally rather than by the model: the opener is the same every
    // time, so a round-trip buys a spinner and a bill for a greeting. It still
    // joins the history, so the model sees it on the next turn.
    addMessage('assistant', t(opt.replyKey), { isCanned: true });
  };

  return (
    <Affix position={{ bottom: 20, right: 20 }} zIndex={200}>
      {isOpen ? (
        <PanelWrapper isClosing={isClosing} style={{ width: panelSize.width, height: panelSize.height }}>
          <ResizeHandle onMouseDown={handleResizeStart} />
          {/* Header. One line at every panel width: when space runs out the
              title truncates; the status pill and the buttons never shrink. */}
          <Group
            position="apart"
            spacing="xs"
            noWrap
            px="sm"
            py="xs"
            sx={({ colors }) => ({
              borderBottom: `1px solid ${colors.gray[2]}`,
              flexShrink: 0,
            })}
          >
            <Group spacing={6} noWrap sx={{ minWidth: 0 }}>
              <FiMessageSquare size={16} style={{ flexShrink: 0 }} />
              <Text size="sm" weight={600} truncate>
                {t('chat.title')}
              </Text>
              <Text
                size="xs"
                sx={({ colors, radius }) => ({
                  backgroundColor: colors.orange[1],
                  color: colors.orange[7],
                  borderRadius: radius.sm,
                  padding: '1px 6px',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                })}
              >
                {t('chat.status')}
              </Text>
            </Group>
            <Group spacing={4} noWrap sx={{ flexShrink: 0 }}>
              <button
                onClick={handleExport}
                aria-label={t('chat.export')}
                title={t('chat.export')}
                disabled={messages.length === 0}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: messages.length === 0 ? 'not-allowed' : 'pointer',
                  color: messages.length === 0 ? '#dee2e6' : '#adb5bd',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => { if (messages.length > 0) e.currentTarget.style.color = '#228be6'; }}
                onMouseLeave={(e) => { if (messages.length > 0) e.currentTarget.style.color = '#adb5bd'; }}
              >
                <FiDownload size={13} />
              </button>
              <button
                onClick={() => setHistoryMode((v) => !v)}
                aria-label={t('chat.history')}
                title={t('chat.history')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: historyMode ? '#228be6' : '#adb5bd',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#228be6')}
                onMouseLeave={(e) => (e.currentTarget.style.color = historyMode ? '#228be6' : '#adb5bd')}
              >
                <FiClock size={13} />
              </button>
              <button
                onClick={() => { saveSession(name); clearMessages(); setHistoryMode(false); }}
                aria-label={t('chat.clear')}
                title={t('chat.clear')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#adb5bd',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#fa5252')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#adb5bd')}
              >
                <FiTrash2 size={13} />
              </button>
              <button
                onClick={handleClose}
                aria-label={t('chat.close')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#868e96',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 4,
                  borderRadius: 4,
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#212529')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#868e96')}
              >
                <FiX size={14} />
              </button>
            </Group>
          </Group>

          {/* The assistant doesn't work reliably yet. Said plainly and kept in
              view, rather than a "beta" label that reads as mostly working. */}
          <Alert
            icon={<FiAlertTriangle size={14} />}
            color="orange"
            variant="light"
            radius={0}
            p="xs"
            sx={{ flexShrink: 0 }}
          >
            <Text size="xs">
              <Trans
                i18nKey="chat.notice"
                components={{
                  docs: DOCS_USER_BASE_URL ? (
                    <Anchor href={DOCS_USER_BASE_URL} target="_blank" rel="noopener noreferrer" inherit />
                  ) : (
                    <span />
                  ),
                }}
              />
            </Text>
          </Alert>

          {/* History view */}
          {historyMode && (
            history.length === 0 ? (
              <HistoryEmpty>{t('chat.history.empty')}</HistoryEmpty>
            ) : (
              <HistoryList>
                {history.map((session) => (
                  <HistoryItem key={session.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {session.intent && (
                        <HistoryItemBadge intent={session.intent}>
                          {session.intent.replace('-', ' ')}
                        </HistoryItemBadge>
                      )}
                      {session.templateName && (
                        <HistoryItemBadge intent="">
                          {session.templateName}
                        </HistoryItemBadge>
                      )}
                    </div>
                    <HistoryItemTitle>{session.title}</HistoryItemTitle>
                    <HistoryItemMeta>
                      {new Date(session.savedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                      {' · '}
                      {session.messages.filter(m => !m.isError).length} messages
                    </HistoryItemMeta>
                    <HistoryItemActions>
                      <CopyButton
                        onClick={() => { loadSession(session.id); setHistoryMode(false); }}
                        title={t('chat.history.restore')}
                      >
                        <FiCornerDownLeft size={11} /> {t('chat.history.restore')}
                      </CopyButton>
                      <CopyButton
                        onClick={() => deleteSession(session.id)}
                        title={t('chat.clear')}
                      >
                        <FiTrash2 size={11} />
                      </CopyButton>
                    </HistoryItemActions>
                  </HistoryItem>
                ))}
              </HistoryList>
            )
          )}

          {/* Messages */}
          {!historyMode && <MessageList ref={listRef} onScroll={handleScroll}>
            {messages.length === 0 && !intent && (
              <WelcomeScreen>
                <WelcomeTitle>{t('chat.welcome.title')}</WelcomeTitle>
                <OptionGrid>
                  {CHAT_OPTIONS.map((opt) => (
                    <OptionCard key={opt.id} onClick={() => handleOptionSelect(opt)}>
                      {opt.icon}
                      <OptionCardTitle>{t(opt.titleKey)}</OptionCardTitle>
                      <OptionCardDesc>{t(opt.descKey)}</OptionCardDesc>
                    </OptionCard>
                  ))}
                </OptionGrid>
              </WelcomeScreen>
            )}
            {messages.map((msg) =>
              msg.role === 'user' ? (
                <BubbleWrapper key={msg.id} align="right">
                  <UserBubble>{msg.content}</UserBubble>
                  <Timestamp style={{ textAlign: 'right' }}>{formatTime(msg.timestamp)}</Timestamp>
                </BubbleWrapper>
              ) : msg.isError ? (
                <BubbleWrapper key={msg.id} align="left">
                  <ErrorBubble>{msg.content}</ErrorBubble>
                  <BubbleActions className="bubble-actions">
                    <CopyButton onClick={() => handleRetry(msg.id)}>
                      <FiRefreshCw size={11} /> Retry
                    </CopyButton>
                    <Timestamp>{formatTime(msg.timestamp)}</Timestamp>
                  </BubbleActions>
                </BubbleWrapper>
              ) : (
                <BubbleWrapper key={msg.id} align="left">
                  <AssistantBubble>{msg.content}</AssistantBubble>
                  <BubbleActions className="bubble-actions">
                    <CopyButton
                      copied={copiedId === msg.id}
                      onClick={() => handleCopy(msg.id, msg.content)}
                    >
                      {copiedId === msg.id ? <FiCheck size={11} /> : <FiCopy size={11} />}
                      {copiedId === msg.id ? 'Copied' : 'Copy'}
                    </CopyButton>
                    <ParticleWrap>
                      <FeedbackButton
                        active={msg.feedback === 'up'}
                        positive
                        springing={(animCounters[`${msg.id}-up`] ?? 0) > 0}
                        onClick={() => handleFeedback(msg.id, 'up')}
                        title="Good response"
                      >
                        <FiThumbsUp size={11} />
                      </FeedbackButton>
                      {(animCounters[`${msg.id}-up`] ?? 0) > 0 && (
                        <ParticleOrigin key={animCounters[`${msg.id}-up`]}>
                          {PARTICLE_ANGLES.map((angle, i) => (
                            <Particle
                              key={angle}
                              positive
                              style={{ '--angle': `${angle}deg`, animationDelay: `${i * 0.015}s` } as React.CSSProperties}
                            />
                          ))}
                        </ParticleOrigin>
                      )}
                    </ParticleWrap>
                    <ParticleWrap>
                      <FeedbackButton
                        active={msg.feedback === 'down'}
                        springing={(animCounters[`${msg.id}-down`] ?? 0) > 0}
                        onClick={() => handleFeedback(msg.id, 'down')}
                        title="Bad response"
                      >
                        <FiThumbsDown size={11} />
                      </FeedbackButton>
                      {(animCounters[`${msg.id}-down`] ?? 0) > 0 && (
                        <ParticleOrigin key={animCounters[`${msg.id}-down`]}>
                          {PARTICLE_ANGLES.map((angle, i) => (
                            <Particle
                              key={angle}
                              positive={false}
                              style={{ '--angle': `${angle}deg`, animationDelay: `${i * 0.015}s` } as React.CSSProperties}
                            />
                          ))}
                        </ParticleOrigin>
                      )}
                    </ParticleWrap>
                    {msg.id === lastAssistantId && !isLoading && !msg.isCanned && (
                      <CopyButton onClick={() => handleRegenerate(msg.id)} title="Regenerate">
                        <FiRefreshCw size={11} /> Regenerate
                      </CopyButton>
                    )}
                    <Timestamp>{formatTime(msg.timestamp)}</Timestamp>
                  </BubbleActions>
                </BubbleWrapper>
              ),
            )}
            {isLoading && (
              <TypingIndicator>
                <TypingDot delay={0} />
                <TypingDot delay={0.2} />
                <TypingDot delay={0.4} />
              </TypingIndicator>
            )}
            <div ref={bottomRef} />
          </MessageList>}
          {!historyMode && (
            <ScrollToBottomBtn visible={showScrollBtn} onClick={scrollToBottom}>
              <FiChevronDown size={14} />
            </ScrollToBottomBtn>
          )}

          {/* Input */}
          {!historyMode && <InputRow>
            <TextInput
              sx={({ colors }) => ({
                flex: 1,
                '& input': { transition: 'border-color 0.15s ease, box-shadow 0.15s ease' },
                '& input:focus': {
                  borderColor: colors.blue[5],
                  boxShadow: `0 0 0 2px ${colors.blue[1]}`,
                },
              })}
              placeholder={intent ? t('chat.placeholder') : t('chat.placeholderLocked')}
              value={input}
              disabled={isLoading || !intent}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              size="sm"
            />
            <SendButton
              onClick={handleSend}
              disabled={!input.trim() || isLoading || !intent}
              aria-label={t('chat.send')}
            >
              <FiSend size={14} />
            </SendButton>
          </InputRow>}
        </PanelWrapper>
      ) : (
        <FABWrapper>
          <FAB
            onClick={toggleChat}
            aria-label={`${t('chat.title')} (${t('chat.status')})`}
            title={`${t('chat.title')} (${t('chat.status')})`}
          >
            <FiMessageSquare size={22} />
          </FAB>
          {unreadCount > 0 && (
            <UnreadBadge>{unreadCount > 9 ? '9+' : unreadCount}</UnreadBadge>
          )}
        </FABWrapper>
      )}
    </Affix>
  );
};
